import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Building2, ChevronLeft, ChevronRight, MapPin, Bed, Bath, Square, Eye } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PropertyCarousel() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showingOwn, setShowingOwn] = useState(false);

  useEffect(() => {
    fetchDashboardProperties();
  }, []);

  const fetchDashboardProperties = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/properties/dashboard`, { headers });
      setProperties(response.data.properties || []);
      setShowingOwn(response.data.showing_own || false);
    } catch (error) {
      console.error("Failed to load dashboard properties:", error);
    } finally {
      setLoading(false);
    }
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? properties.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === properties.length - 1 ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-md border border-slate-200 shadow-sm p-8">
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="bg-white rounded-md border border-slate-200 shadow-sm" data-testid="property-carousel-empty">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            {showingOwn ? "My Properties" : "Featured Properties"}
          </h2>
        </div>
        <div className="p-12 text-center">
          <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 mb-4">
            {showingOwn ? "You haven't added any properties yet" : "No properties available"}
          </p>
          <Button
            data-testid="add-first-property-btn"
            onClick={() => navigate("/properties/new")}
            className="bg-blue-900 hover:bg-blue-800 text-white rounded-sm"
          >
            Add Your First Property
          </Button>
        </div>
      </div>
    );
  }

  const currentProperty = properties[currentIndex];

  return (
    <div className="bg-white rounded-md border border-slate-200 shadow-sm" data-testid="property-carousel">
      {/* Header */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              {showingOwn ? "My Properties" : "Featured Properties"}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {showingOwn ? "Your recently added properties" : "Latest properties in the system"}
            </p>
          </div>
          <Button
            data-testid="view-all-properties-btn"
            onClick={() => navigate("/properties")}
            variant="outline"
            className="border-blue-900 text-blue-900 hover:bg-blue-50 rounded-sm"
          >
            View All
          </Button>
        </div>
      </div>

      {/* Carousel Content */}
      <div className="relative">
        {/* Main Content */}
        <div className="flex flex-col md:flex-row">
          {/* Property Image */}
          <div className="md:w-1/2 relative">
            {currentProperty.media_files && currentProperty.media_files.length > 0 ? (
              <img
                src={currentProperty.media_files[0]}
                alt={currentProperty.title}
                className="w-full h-64 md:h-80 object-cover"
                data-testid="carousel-property-image"
              />
            ) : (
              <div className="w-full h-64 md:h-80 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                <Building2 className="h-20 w-20 text-slate-400" />
              </div>
            )}
            {/* Status Badge */}
            <div className="absolute top-4 left-4">
              <span className={`px-3 py-1 text-xs font-semibold rounded-sm uppercase tracking-wide ${
                currentProperty.status === 'available' 
                  ? 'bg-green-100 text-green-800' 
                  : currentProperty.status === 'sold'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-amber-100 text-amber-800'
              }`}>
                {currentProperty.status}
              </span>
            </div>
          </div>

          {/* Property Details */}
          <div className="md:w-1/2 p-6 flex flex-col justify-between">
            <div>
              <div className="flex items-start justify-between mb-2">
                <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-sm uppercase">
                  {currentProperty.property_type}
                </span>
                <span className="text-xs text-slate-500 uppercase tracking-wide">
                  {currentProperty.purpose}
                </span>
              </div>
              
              <h3 
                className="text-xl font-semibold text-slate-900 mb-2 line-clamp-2" 
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="carousel-property-title"
              >
                {currentProperty.title}
              </h3>
              
              <div className="flex items-center text-slate-600 mb-4">
                <MapPin className="h-4 w-4 mr-1 text-amber-600" />
                <span className="text-sm">{currentProperty.location}</span>
              </div>

              {/* Property Specs */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                {currentProperty.bedrooms && (
                  <div className="flex items-center text-slate-600">
                    <Bed className="h-4 w-4 mr-2 text-blue-900" />
                    <span className="text-sm">{currentProperty.bedrooms} Beds</span>
                  </div>
                )}
                {currentProperty.bathrooms && (
                  <div className="flex items-center text-slate-600">
                    <Bath className="h-4 w-4 mr-2 text-blue-900" />
                    <span className="text-sm">{currentProperty.bathrooms} Baths</span>
                  </div>
                )}
                {currentProperty.area && (
                  <div className="flex items-center text-slate-600">
                    <Square className="h-4 w-4 mr-2 text-blue-900" />
                    <span className="text-sm">{currentProperty.area} sq.ft</span>
                  </div>
                )}
              </div>

              {/* Price */}
              <div className="mb-4">
                <p className="text-2xl font-bold text-blue-900" data-testid="carousel-property-price">
                  ₹{currentProperty.price?.toLocaleString('en-IN')}
                </p>
                {currentProperty.rate && currentProperty.rate_amount && (
                  <p className="text-sm text-slate-500">
                    ₹{currentProperty.rate_amount?.toLocaleString('en-IN')}/{currentProperty.rate}
                  </p>
                )}
              </div>
            </div>

            {/* Action Button */}
            <Button
              data-testid="view-property-details-btn"
              onClick={() => navigate(`/properties/view/${currentProperty.id}`)}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
            >
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </Button>
          </div>
        </div>

        {/* Navigation Arrows */}
        {properties.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-800 p-2 rounded-full shadow-lg transition-all hover:scale-110"
              data-testid="carousel-prev-btn"
              aria-label="Previous property"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-800 p-2 rounded-full shadow-lg transition-all hover:scale-110"
              data-testid="carousel-next-btn"
              aria-label="Next property"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {/* Dot Indicators */}
      {properties.length > 1 && (
        <div className="flex justify-center items-center gap-2 p-4 border-t border-slate-100">
          {properties.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-blue-900 w-6' 
                  : 'bg-slate-300 hover:bg-slate-400'
              }`}
              data-testid={`carousel-dot-${index}`}
              aria-label={`Go to property ${index + 1}`}
            />
          ))}
          <span className="text-xs text-slate-500 ml-3">
            {currentIndex + 1} / {properties.length}
          </span>
        </div>
      )}
    </div>
  );
}
